import '@fortawesome/fontawesome-free/css/all.min.css';
import { useEffect, useRef, useState, useMemo } from 'react';

// * D3 and D3-Org-Chart
import * as d3 from 'd3';
import { OrgChart } from 'd3-org-chart';

// * MUI
import {
  Box,
  IconButton,
  InputAdornment,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles'; // Import MUI's useTheme hook

// * MUI Icons
import ClearRoundedIcon from '@mui/icons-material/ClearRounded';
import CloseFullscreenRoundedIcon from '@mui/icons-material/CloseFullscreenRounded';
import FilterAltRoundedIcon from '@mui/icons-material/FilterAltRounded';
import FitScreenRoundedIcon from '@mui/icons-material/FitScreenRounded';
import KeyboardTabRoundedIcon from '@mui/icons-material/KeyboardTabRounded';
import OpenWithRoundedIcon from '@mui/icons-material/OpenWithRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import ZoomInRoundedIcon from '@mui/icons-material/ZoomInRounded';
import ZoomOutRoundedIcon from '@mui/icons-material/ZoomOutRounded';

//* Components
import ClickedNodeDialog from './ClickedNodeDialog';
import CreateRootNodeDialog from './CreateRootNodeDialog';
import NoTreeFoundCard from './NoTreeFoundCard';
import TreeGraphFilterInput from './TreeGraphFilterInput';

import { selectTimeRange } from '@/features/energyxplorer/redux/timeRangeSlice';
import { useSelector } from 'react-redux';
import { getButtonContent, getNodeContent } from './chartRenderes';

// * i18n
import { useTranslation } from 'react-i18next';

// * Utils
import { getGroupedOptions } from '../../utils/nodeTypeMapper'; // Adjust the import path as necessary
import { validateTree } from '../../utils/energyTreeFunctions';

const makeObjectsExtensible = (data) => {
  return data.map((node) => {
    return { ...node }; // Creates a shallow copy, ensuring it's extensible
  });
};

const OrgChartComponent = ({
  selectedEnergyGroup,
  data,
  units,
  setNodeToAnalyse,
  setValue,
  setFilters,
}) => {
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null); // Ref for the OrgChart instance
  const { t } = useTranslation('energyxplorer-tree-tab');
  const timeRange = useSelector(selectTimeRange);
  const theme = useTheme(); // Access the current MUI theme
  const [searchValue, setSearchValue] = useState('');
  const debounceTimeoutRef = useRef(null);

  const [filterDialogOpen, setFilterDialogOpen] = useState(false);

  const [localData, setLocalData] = useState(() => makeObjectsExtensible(data));

  const [chartConfig, setChartConfig] = useState({
    showKpiGauge: false,
    showUndocumentedConsumption: false,
  });

  const [clickedNode, setClickedNode] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [createFirstNodeDialogOpen, setCreateFirstNodeDialogOpen] =
    useState(false);

  const [expandLevel, setExpandLevel] = useState(1); // show root + depth 1 initially
  const [treeMaxDepth, setTreeMaxDepth] = useState(0); // Track the maximum depth of the tree

  const { invalidNodeIds, errors } = useMemo(() => validateTree(data), [data]);

  useEffect(() => {
    if (!selectedEnergyGroup || !selectedEnergyGroup.energyTreeNodes) return;

    const updatedData = makeObjectsExtensible(
      selectedEnergyGroup.energyTreeNodes,
    ).map((node) => {
      const existingNode = localData?.find(
        (n) => n.energyTreeNodeId === node.energyTreeNodeId,
      );

      return {
        ...node,
        _expanded: existingNode?._expanded || true, // Preserve expanded state
        _highlighted: false, // Clear highlights
      };
    });

    if (chartInstanceRef.current) {
      chartInstanceRef.current.data(updatedData).render(); // Update and re-render the chart
      setExpandToLevel(expandLevel);
    }
  }, [expandLevel, localData, selectedEnergyGroup]);

  const initOrUpdateChart = () => {
    const container = chartRef.current;
    if (!container) return;

    const isDarkMode = theme.palette.mode === 'dark';

    if (!chartInstanceRef.current) {
      // First-time initialization
      chartInstanceRef.current = new OrgChart()
        .container(container)
        .nodeId((d) => d.energyTreeNodeId)
        .parentNodeId((d) => d.parentId)
        .setActiveNodeCentered(true)
        .nodeHeight(() => 150)
        .nodeWidth(() => 300)
        .childrenMargin(() => 100)
        .compactMarginBetween(() => 20)
        .neighbourMargin(() => 100)
        .siblingsMargin(() => 75)
        .compactMarginPair(() => 100)
        .onNodeClick((node) => handleNodeClick(node))
        .linkUpdate(function (d) {
          d3.select(this)
            .attr(
              'stroke',
              d.data._upToTheRootHighlighted ? '#4CAF50' : '#E4E2E9',
            )
            .attr('stroke-width', d.data._upToTheRootHighlighted ? 4 : 1);
          if (d.data._upToTheRootHighlighted) {
            d3.select(this).raise();
          }
        })
        .nodeUpdate(function (d, i, arr, state) {
          d3.select(this)
            .select('.node-content')
            .style('color', (d) =>
              d.data._highlighted ? '#000' : isDarkMode ? '#E4E2E9' : '#08011E',
            )
            .style('box-shadow', (d) =>
              d.data._highlighted
                ? '0 0 0 4px #4CAF50'
                : d.data._upToTheRootHighlighted
                  ? '0 0 0 4px #4CAF50'
                  : 'none',
            )
            .style('border-radius', '16px')
            .style('transition', 'box-shadow 0.3s ease-in-out');

          d3.select(chartRef.current)
            .select('svg')
            .attr('style', 'height: 100%; overflow: hidden;');
        });
    }

    // Always update data and dynamic renderers
    chartInstanceRef.current
      .data(localData)
      .nodeContent(
        getNodeContent(
          isDarkMode,
          chartConfig.showKpiGauge,
          chartConfig.showUndocumentedConsumption,
          timeRange,
          invalidNodeIds,
          errors,
        ),
      )
      .buttonContent(getButtonContent(isDarkMode))
      .render();
    setExpandToLevel(expandLevel);
  };

  useEffect(() => {
    if (!chartInstanceRef.current) {
      initOrUpdateChart();
    }
    const allNodes = chartInstanceRef.current?.getChartState()?.allNodes || [];
    const maxDepth = allNodes.reduce((m, n) => Math.max(m, n.depth), 0);

    setTreeMaxDepth(maxDepth);
  }, []);

  useEffect(() => {
    if (!chartInstanceRef.current) return;

    const isDarkMode = theme.palette.mode === 'dark';

    // Update the node/button content renderers
    chartInstanceRef.current
      .nodeContent(
        getNodeContent(
          isDarkMode,
          chartConfig.showKpiGauge,
          chartConfig.showUndocumentedConsumption,
          timeRange,
          invalidNodeIds,
          errors,
        ),
      )
      .buttonContent(getButtonContent(isDarkMode))
      .render(); // Re-render without replacing data

    setExpandToLevel(expandLevel);
  }, [theme, chartConfig, expandLevel, timeRange]);

  const handleNodeClick = (node) => {
    if (!node?.data?.energyTreeNodeId) return;
    setClickedNode(node.data);
    setDialogOpen(true);
  };

  const setExpandToLevel = (level) => {
    if (!chartInstanceRef.current) return;
    const chart = chartInstanceRef.current;
    const { allNodes } = chart.getChartState();
    if (!allNodes) return;

    const targetDepth = Math.max(1, level);
    setExpandLevel(targetDepth);

    allNodes.forEach((node) => {
      if (node.depth <= targetDepth) {
        // expand this node
        if (node._children) {
          node.children = node._children;
          node._children = null;
        }
        node.data._expanded = true;
      } else {
        // collapse this node
        if (node.children) {
          node._children = node.children;
          node.children = null;
        }
        node.data._expanded = false;
      }
    });

    chart.render().fit();
  };

  const expandOneLevel = () => {
    // optional: compute max depth to avoid overshooting
    const allNodes = chartInstanceRef.current?.getChartState()?.allNodes || [];
    const maxDepth = allNodes.reduce((m, n) => Math.max(m, n.depth), 0);
    setExpandToLevel(Math.min(expandLevel + 1, maxDepth));
    setTreeMaxDepth(maxDepth);
  };

  const collapseOneLevel = () => {
    if (expandLevel <= 1) return; // prevent collapsing below level 1
    setExpandToLevel(expandLevel - 1);
  };

  const collapseAllExceptDepthOne = () => setExpandToLevel(1);

  const expandAllNodes = () => {
    if (chartInstanceRef.current) {
      const allNodes =
        chartInstanceRef.current?.getChartState()?.allNodes || [];
      const maxDepth = allNodes.reduce((m, n) => Math.max(m, n.depth), 0);
      chartInstanceRef.current.expandAll();
      chartInstanceRef.current.fit();
      setExpandToLevel(maxDepth); // Set to max depth after expanding all
    }
  };

  const zoomIn = () => {
    if (chartInstanceRef.current) {
      chartInstanceRef.current.zoomIn();
    }
  };

  const zoomOut = () => {
    if (chartInstanceRef.current) {
      chartInstanceRef.current.zoomOut();
    }
  };

  const collapseAllNodes = () => {
    collapseAllExceptDepthOne();
  };

  const fitToScreen = () => {
    if (chartInstanceRef.current) {
      chartInstanceRef.current.fit();
    }
  };

  const handleSearch = (value) => {
    setSearchValue(value);

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      if (!chartInstanceRef.current) return;

      const chart = chartInstanceRef.current;
      const data = chart.data();

      data.forEach((node) => {
        node._highlighted = false;
        node._upToTheRootHighlighted = false;
        node._expanded = false;
      });

      let firstMatch = null;

      const markAncestors = (node) => {
        node._upToTheRootHighlighted = true;
        node._expanded = true;
        if (node.parentId) {
          const parent = data.find((n) => n.energyTreeNodeId === node.parentId);
          if (parent) markAncestors(parent);
        }
      };

      data.forEach((node) => {
        const name = node.name?.toLowerCase() || '';
        const search = value?.toLowerCase() || '';

        if (search && name.includes(search)) {
          node._highlighted = true;
          markAncestors(node);
          if (!firstMatch) firstMatch = node;
        }
      });

      if (!value) {
        data.forEach((node) => {
          node._expanded = true;
        });
      }

      chart.data(data).render().fit();

      if (firstMatch) {
        chart.centerNodeById(firstMatch.energyTreeNodeId);
      }
    }, 200);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setClickedNode(null);
    setNodeToAnalyse(null);
  };

  // ========== CSV Download Handler ========== //

  const handleDownload = () => {
    if (!clickedNode?.options?.data.length) return;

    const header = 'Name,Timestamp,Value\n';
    const rows = clickedNode?.options?.data
      .map((d) => `${clickedNode?.name},${d.Timestamp},${d.Value},${d.Base}`)
      .join('\n');

    const csv = header + rows;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = `${clickedNode?.name}_data.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const groupedOptions = getGroupedOptions();

  return (
    <>
      {!selectedEnergyGroup?.energyTreeNodes?.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mt: 8,
          }}
        >
          <NoTreeFoundCard
            setCreateFirstNodeDialogOpen={setCreateFirstNodeDialogOpen}
            selectedEnergyGroup={selectedEnergyGroup}
          />
        </Box>
      )}
      <Box
        className="non-printable"
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          overflow: 'hidden',
          mb: 1,
          p: 0,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 1,
          }}
        >
          {selectedEnergyGroup?.energyTreeNodes?.length > 0 && (
            <TextField
              type="search"
              variant="outlined"
              label={t('tree_actions.search_node')}
              size="small"
              value={searchValue}
              onChange={(e) => handleSearch(e.target.value)}
              sx={{
                minWidth: '300px',
                borderRadius: 3,
                backgroundColor:
                  theme.palette.mode === 'dark' ? '#121212' : '#FFFFFF',
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3, // <-- this rounds the outer container
                  '& fieldset': {
                    borderRadius: 3, // <-- this rounds the actual border
                  },
                  '&:hover fieldset': {
                    border: '1px solid',
                    borderColor: 'divider',
                  },
                  '&.Mui-focused fieldset': {
                    border: '1px solid',
                    borderColor: 'divider',
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: 'inherit',
                },
              }}
              InputProps={{
                disableUnderline: true,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon />
                  </InputAdornment>
                ),
                endAdornment: searchValue && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => handleSearch('')}
                      edge="end"
                    >
                      <ClearRoundedIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}
        </Box>

        {selectedEnergyGroup?.energyTreeNodes?.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
              p: 0,
            }}
          >
            <Tooltip title="Zoom in" placement="bottom">
              <ToggleButton
                value="collapse"
                onClick={() => zoomIn()}
                sx={{
                  borderRadius: 3,
                  p: 1.1,
                  mr: 1,
                  ml: 1,
                  backgroundColor: 'background.paper',
                }}
              >
                <ZoomInRoundedIcon />
              </ToggleButton>
            </Tooltip>

            <Tooltip title="Zoom out" placement="bottom">
              <ToggleButton
                value="collapse"
                onClick={() => zoomOut()}
                sx={{
                  borderRadius: 3,
                  p: 1.1,
                  mr: 1,
                  backgroundColor: 'background.paper',
                }}
              >
                <ZoomOutRoundedIcon />
              </ToggleButton>
            </Tooltip>

            <Tooltip title={t('tree_actions.filter_nodes')} placement="bottom">
              <ToggleButton
                onClick={() => setFilterDialogOpen(true)}
                sx={{
                  backgroundColor: 'background.paper',
                  borderRadius: 3,
                  p: 1.1,
                  mr: 1,
                }}
              >
                <FilterAltRoundedIcon />
              </ToggleButton>
            </Tooltip>

            <ToggleButtonGroup
              exclusive={false}
              sx={{ backgroundColor: 'background.paper', borderRadius: 3 }}
            >
              <Tooltip title={t('tree_actions.fit_tree')} placement="bottom">
                <ToggleButton
                  value="fit"
                  onClick={fitToScreen}
                  sx={{ borderRadius: 3, p: 1.1 }}
                >
                  <FitScreenRoundedIcon />
                </ToggleButton>
              </Tooltip>

              <Tooltip title={t('tree_actions.expand_all')} placement="bottom">
                <ToggleButton
                  value="expand"
                  onClick={expandAllNodes}
                  disabled={expandLevel === treeMaxDepth}
                  sx={{ borderRadius: 3, p: 1.1 }}
                >
                  <OpenWithRoundedIcon />
                </ToggleButton>
              </Tooltip>

              <Tooltip
                title={t('tree_actions.collapse_all')}
                placement="bottom"
              >
                <ToggleButton
                  value="collapse"
                  onClick={collapseAllNodes}
                  disabled={expandLevel === 1}
                  sx={{ borderRadius: 3, p: 1.1 }}
                >
                  <CloseFullscreenRoundedIcon />
                </ToggleButton>
              </Tooltip>

              <Tooltip title="Fold et niveau op" placement="bottom">
                <ToggleButton
                  value="collapse-one"
                  onClick={collapseOneLevel}
                  disabled={expandLevel <= 1}
                  sx={{ borderRadius: 3, p: 1.1 }}
                >
                  <KeyboardTabRoundedIcon
                    sx={{ transform: 'rotate(-90deg)' }}
                  />
                </ToggleButton>
              </Tooltip>

              <Tooltip title="Fold et niveau ud" placement="bottom">
                <ToggleButton
                  value="expand-one"
                  disabled={expandLevel >= treeMaxDepth}
                  onClick={expandOneLevel}
                  sx={{ borderRadius: 3, p: 1.1 }}
                >
                  <KeyboardTabRoundedIcon sx={{ transform: 'rotate(90deg)' }} />
                </ToggleButton>
              </Tooltip>
            </ToggleButtonGroup>
          </Box>
        )}
      </Box>

      <Box
        ref={chartRef}
        className="chart-container"
        sx={{
          width: '100%',
          height: 'calc(100vh - 235px)',
        }}
      ></Box>

      <ClickedNodeDialog
        energyGroup={selectedEnergyGroup}
        dialogOpen={dialogOpen}
        clickedNode={clickedNode}
        handleClose={handleClose}
        handleDownload={handleDownload}
        groupedOptions={groupedOptions}
        units={units}
        localData={localData}
        setValue={setValue}
      />

      <CreateRootNodeDialog
        open={createFirstNodeDialogOpen}
        onClose={() => setCreateFirstNodeDialogOpen(false)}
        energyGroup={selectedEnergyGroup}
      />

      <TreeGraphFilterInput
        open={filterDialogOpen}
        onClose={() => setFilterDialogOpen(false)}
        viewMode="graph"
        context="energyTree"
        handleApply={(filters) => {
          setFilters(filters); // Should include keys: active_period_filter, active_days_filter, holidays_filter
        }}
        selectedEnergyGroup={selectedEnergyGroup}
      />
    </>
  );
};

export default OrgChartComponent;
